/**
 * Vista de la RÚBRICA del proyecto final — el corazón de M2: el alumno ve los
 * criterios, los descriptores de cada nivel y los puntos ANTES de entregar, para
 * saber exactamente cómo se le evaluará. Tras la corrección, resalta el nivel/
 * puntos obtenidos por criterio (`rubricScores`).
 */
import { type Component, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { ListChecks } from "lucide-solid";
import { normalizeRubric, type Rubric } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";

/** Total de puntos posibles de la rúbrica (suma del nivel máximo por criterio). */
export function rubricMaxPoints(rubric: Rubric): number {
  return rubric.criteria.reduce(
    (sum, c) => sum + c.levels.reduce((m, l) => Math.max(m, l.points), 0),
    0,
  );
}

export const RubricPreview: Component<{
  /** Rúbrica cruda (jsonb del servidor); se normaliza aquí. */
  rubric: unknown;
  /** Puntos obtenidos por criterio tras la corrección (opcional). */
  rubricScores?: Record<string, number> | null;
}> = (props) => {
  const { t } = useI18n();
  const rubric = () => normalizeRubric(props.rubric);

  return (
    <Show
      when={rubric()}
      fallback={
        <p class="text-sm text-muted-foreground" data-testid="final-project-rubric-empty">
          {t(msg`No rubric has been published for this project yet.`)}
        </p>
      }
    >
      {(r) => (
        <div class="space-y-3" data-testid="final-project-rubric">
          <div class="flex items-center gap-1.5 text-sm font-bold text-accent">
            <ListChecks class="h-4 w-4" />
            {t(msg`Evaluation rubric`)}
            <span class="text-xs font-medium text-muted-foreground">
              {(() => {
                const total = rubricMaxPoints(r());
                return t(msg`(${total} pts total)`);
              })()}
            </span>
          </div>
          <For each={r().criteria}>
            {(criterion) => {
              const achieved = (): number | undefined => props.rubricScores?.[criterion.name];
              const achievedPts = (): number => achieved() ?? 0;
              return (
                <div
                  class="rounded-xl border border-border bg-surface p-3"
                  data-testid="final-project-rubric-criterion"
                >
                  <div class="flex items-baseline justify-between gap-2">
                    <span class="font-semibold">{criterion.name}</span>
                    <Show when={achieved() !== undefined}>
                      <span
                        class="shrink-0 rounded-full bg-joy-mint/15 px-2 py-0.5 text-xs font-bold text-joy-mint"
                        data-testid="final-project-rubric-achieved"
                      >
                        {(() => {
                          const pts = achievedPts();
                          return t(msg`${pts} pts`);
                        })()}
                      </span>
                    </Show>
                  </div>
                  <Show when={criterion.description}>
                    <p class="mt-0.5 text-xs text-muted-foreground">{criterion.description}</p>
                  </Show>
                  <div class="mt-2 flex flex-wrap gap-1.5">
                    <For each={criterion.levels}>
                      {(level) => (
                        <span
                          class="inline-flex items-center gap-1 rounded-lg bg-muted px-2 py-1 text-xs"
                          data-testid="final-project-rubric-level"
                        >
                          <span class="font-medium">{level.label}</span>
                          <span class="font-bold text-accent">{level.points}</span>
                        </span>
                      )}
                    </For>
                  </div>
                </div>
              );
            }}
          </For>
        </div>
      )}
    </Show>
  );
};
